import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { usePageChromeSlot } from 'contexts/PageChromeContext';
import { ACCOUNT_RESERVE, BAR_GAP, BAR_HEIGHT, BAR_TOP } from 'dashboard/layouts/admin/chromeGeometry';

/**
 * The top of the content region: which section you are in, and what you can
 * do to it.
 *
 * Rendered by the layout on every screen, not by pages that happen to ask for
 * it. Seven pages did not, so on those the content began at the very top and
 * slid under the account cluster as you scrolled — the band has to be there
 * whether or not anything is in it.
 *
 * Sticky, so the title and Publish stay put while the page scrolls. The outer
 * box is opaque and reaches the top of the window, which is what stops the
 * page appearing above the bar or in the gap beside it: content scrolls
 * behind this, never past it.
 *
 * The white surface stops short of the account cluster, and of the panel
 * below it. Separate things, separate surfaces, with space between them: the
 * bar and the panel used to share an edge, which made one tall shape with a
 * seam across it rather than a title above a page.
 */
export default function PageChromeBar({ leading = null }) {
  const { title, subtitle, setActionSlot, setLeadingSlot } = usePageChromeSlot();

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        // Opaque to the top of the window, so nothing scrolls into view above
        // the bar or through the gap between it and the account cluster. It
        // has to be the page's own ground: anything else is a band of a
        // slightly different colour across the top of every screen.
        bgcolor: 'background.default',
        pt: { xs: `${BAR_TOP.xs}px`, md: `${BAR_TOP.md}px` },
        // The gap under the bar belongs to the bar, not to the panel: this
        // box is the sticky one, so its background is what the page scrolls
        // behind. A margin on the panel instead would leave a transparent
        // stripe for content to show through as it passed.
        pb: BAR_GAP,
      }}
    >
      <Box
        sx={{
          bgcolor: 'background.paper',
          // All four. The flat bottom existed to meet the panel below; they
          // no longer touch.
          borderRadius: '20px',
          minWidth: 0,
          minHeight: BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          // Margin, not padding: the surface has to actually end, or the
          // account cluster floats on top of the section's own white and
          // reads as part of it. Zero on a phone, where the cluster is on its
          // own line above instead of beside it; smaller at xl, where the
          // sidebar is permanent and the menu button is gone.
          mr: {
            xs: `${ACCOUNT_RESERVE.xs}px`,
            md: `${ACCOUNT_RESERVE.md}px`,
            xl: `${ACCOUNT_RESERVE.xl}px`,
          },
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          gap='12px'
          sx={{ px: { xs: '16px', md: '22px' }, width: '100%', minWidth: 0 }}
        >
          {leading}
          {/* A screen inside another one puts its way back and its own name
              here — see PageHeader's `leading`. */}
          <Stack
            ref={setLeadingSlot}
            direction='row'
            alignItems='center'
            spacing='8px'
            sx={{ minWidth: 0 }}
          />
          {/* The title and its line of explanation, once. Both used to be
              repeated at the top of the panel directly below this. */}
          {(title || subtitle) && (
            <Box sx={{ minWidth: 0 }}>
              {title && (
                <Typography variant='subtitle1' fontWeight='700' noWrap lineHeight={1.3}>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  noWrap
                  display='block'
                  lineHeight={1.4}
                  // Gone on a phone, where the row has no width to spare and
                  // the title alone still says where you are.
                  sx={{ display: { xs: 'none', md: 'block' } }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
          {/* The page portals its buttons in here, at the far end so they sit
              nearest the account cluster rather than adrift in the middle. */}
          <Stack
            ref={setActionSlot}
            direction='row'
            alignItems='center'
            spacing='8px'
            sx={{ minWidth: 0, ml: 'auto' }}
          />
        </Stack>
      </Box>
    </Box>
  );
}
